// pages/Register.jsx - Updated to match backend fields (name, phone, altphone, email, address)
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { STORAGE_KEYS } from "../utils/constants";
import { api, showNotification, NOTIFICATION_TYPES } from "../utils/apiClient";

// ✅ MODERN: Success Animation with glassmorphism (same as your file)
const SuccessAnimation = ({ message }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 max-w-sm mx-4 shadow-2xl border border-white/20 animate-scale-bounce">
        <div className="text-center">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 rounded-full animate-pulse-slow"></div>
            <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  className="animate-check-draw"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                  strokeDasharray="30"
                  strokeDashoffset="30"
                />
              </svg>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-2 animate-slide-up">Success!</h3>
          <p className="text-gray-600 animate-slide-up-delay">{message}</p>
        </div>
      </div>
    </div>
  );
};

// ✅ MODERN: Redirecting Animation (kept)
const RedirectingAnimation = ({ message = "Redirecting..." }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 max-w-sm mx-4 shadow-2xl border border-white/20 animate-scale-bounce">
        <div className="text-center">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0">
              <div className="w-full h-full border-4 border-indigo-200 rounded-full"></div>
              <div className="absolute inset-0 w-full h-full border-4 border-indigo-600 rounded-full border-t-transparent animate-spin-slow"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-600 rounded-full animate-pulse"></div>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-2">{message}</h3>
          <p className="text-gray-600">Please wait a moment...</p>

          <div className="flex justify-center space-x-2 mt-4">
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced OTP Input Component (kept)
const OTPInput = ({ length = 4, onComplete, loading = false, showHint = false }) => {
  const inputRef = useRef([]);
  const [otp, setOTP] = useState(Array(length).fill(""));

  useEffect(() => {
    if (inputRef.current[0]) inputRef.current[0].focus();
  }, []);

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);

    if (value && index < length - 1) inputRef.current[index + 1]?.focus();
    if (!value && index > 0) inputRef.current[index - 1]?.focus();

  if (newOTP.every((digit) => digit !== "") && newOTP.join("").length === length) {
  if (!loading) onComplete(newOTP.join(""));  // ✅ don't fire if already loading
}
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, length);
    if (!/^\d+$/.test(pastedData)) return;

    const newOTP = Array(length).fill("");
    pastedData.split("").forEach((digit, idx) => {
      if (idx < length) newOTP[idx] = digit;
    });
    setOTP(newOTP);

    const lastIndex = Math.min(pastedData.length - 1, length - 1);
    inputRef.current[lastIndex]?.focus();

    if (newOTP.join("").length === length) onComplete(newOTP.join(""));
  };

  const fillTestOTP = () => {
    const testOTP = ["1", "2", "3", "4"];
    setOTP(testOTP);
    setTimeout(() => onComplete(testOTP.join("")), 500);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center gap-3">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={otp[index]}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={(ref) => (inputRef.current[index] = ref)}
            disabled={loading}
            className={`w-14 h-14 text-center text-xl font-semibold border-2 rounded-lg 
            ${otp[index] ? "border-indigo-500 bg-indigo-50" : "border-gray-300"}
            focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 
            disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            placeholder="•"
          />
        ))}
      </div>

      {showHint && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800 font-medium mb-1">Development Mode</p>
              <p className="text-xs text-blue-700 mb-2">
                Use test OTP: <span className="font-mono font-bold">1234</span>
              </p>
              <button
                type="button"
                onClick={fillTestOTP}
                disabled={loading}
                className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded disabled:opacity-50 transition-colors"
              >
                Auto-fill Test OTP
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Register = () => {
  const [step, setStep] = useState("registration"); // "registration" | "otp-verification"
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    altphone: "",
    email: "",
    address: "",
  });

  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [countdown, setCountdown] = useState(0);
  const [developmentMode, setDevelopmentMode] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [showOtpSuccess, setShowOtpSuccess] = useState(false);
  const [showRegisterSuccess, setShowRegisterSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // if login page redirected here with phone
    if (location.state?.phoneNumber) {
      setFormData((prev) => ({ ...prev, phone: String(location.state.phoneNumber) }));
    }
      if (location.state?.returnUrl && !location.search.includes('returnUrl')) {
    const newUrl = `${location.pathname}?returnUrl=${encodeURIComponent(location.state.returnUrl)}`;
    window.history.replaceState({}, '', newUrl);
  }
  }, [location.state]);

  useEffect(() => {
    let timer;
    if (countdown > 0) timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const validateField = (fieldName, value) => {
    const newErrors = { ...validationErrors };

    switch (fieldName) {
      case "name":
        if (!value.trim()) newErrors.name = "Full name is required";
        else if (value.trim().length < 2) newErrors.name = "Name must be at least 2 characters";
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) newErrors.name = "Name can only contain letters and spaces";
        else delete newErrors.name;
        break;

      case "email":
        if (!value.trim()) newErrors.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) newErrors.email = "Please enter a valid email";
        else delete newErrors.email;
        break;

      case "phone":
        if (!value.trim()) newErrors.phone = "Phone number is required";
        else if (!/^[6-9]\d{9}$/.test(value.trim())) newErrors.phone = "Phone must be 10 digits starting with 6-9";
        else delete newErrors.phone;
        break;

      case "altphone":
        if (!value.trim()) newErrors.altphone = "Alternate phone number is required";
        else if (!/^[6-9]\d{9}$/.test(value.trim())) newErrors.altphone = "Alt phone must be 10 digits starting with 6-9";
        else delete newErrors.altphone;
        break;

      case "address":
        if (!value.trim()) newErrors.address = "Address is required";
        else if (value.trim().length < 10) newErrors.address = "Address should be minimum 10 characters";
        else delete newErrors.address;
        break;

      default:
        break;
    }

    setValidationErrors(newErrors);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = "Full name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    if (!formData.altphone.trim()) errors.altphone = "Alternate phone number is required";
    if (!formData.address.trim()) errors.address = "Address is required";

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = "Please enter a valid email";
    }
    if (formData.phone.trim() && !/^[6-9]\d{9}$/.test(formData.phone.trim())) {
      errors.phone = "Phone must be 10 digits starting with 6-9";
    }
    if (formData.altphone.trim() && !/^[6-9]\d{9}$/.test(formData.altphone.trim())) {
      errors.altphone = "Alt phone must be 10 digits starting with 6-9";
    }
    if (formData.address.trim() && formData.address.trim().length < 10) {
      errors.address = "Address should be minimum 10 characters";
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError("Please fix the errors below and try again.");
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    if (name === "phone" || name === "altphone") {
      processedValue = value.replace(/\D/g, "").slice(0, 10);
    }
    if (name === "email") {
      processedValue = value.toLowerCase().trim();
    }
    if (name === "name") {
      processedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    validateField(name, processedValue);

    if (error) setError("");
  };

  // ✅ Step 1: Send OTP (backend expects { phone }) [file:1]
  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await api.post("/user/register/send-otp", {
        phone: formData.phone.trim(),
      });

      const responseData = result?.data || result;

      // backend currently uses OTP=1234; some clients expose developmentMode [file:1]
      setDevelopmentMode(Boolean(responseData?.developmentMode));

      setShowOtpSuccess(true);
      showNotification("OTP sent successfully", NOTIFICATION_TYPES.SUCCESS);

      setTimeout(() => {
        setShowOtpSuccess(false);
        setSuccess(responseData?.message || "OTP sent successfully");
        setStep("otp-verification");
        setCountdown(30);
        window.scrollTo(0, 0);
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to send OTP";
      setError(msg);
      showNotification(msg, NOTIFICATION_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Step 2: Verify OTP + Register (backend expects name, phone, otp, altphone, email, address) [file:1]
const handleVerifyOTP = async (otpCode) => {
  if (!otpCode || otpCode.length !== 4) {
    setError("Please enter a valid 4-digit OTP");
    return;
  }

  setOtpLoading(true);
  setError("");

  try {
    const payload = {
      name: (formData.name ?? "").trim(),
      phone: (formData.phone ?? "").trim(),          // map UI -> backend
      otp: (otpCode ?? "").trim(),
  altphone: (formData.altphone ?? "").trim(),       email: (formData.email ?? "").trim(),
      address: (formData.address ?? "").trim(),
    };

    // IMPORTANT: verify endpoint must be /api/user/register/verify-otp [file:1]
    const result = await api.post("/user/register/verify-otp", payload);
    const token = result?.data?.token;
const user = result?.data?.user;

// ✅ ADD THIS
if (!token) {
  console.error('❌ No token received. Full response:', result);
  setError('Registration error: no token received. Please try again.');
  return;
}
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
if (user) localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

    // success flow same as your existing code...
    setShowRegisterSuccess(true);
    showNotification(result.data?.message || "Registration successful!", NOTIFICATION_TYPES.SUCCESS);

    setTimeout(() => {
navigate("/profile", { replace: true });     }, 2000);

  } catch (err) {
    const msg = err.response?.data?.message || err.message || "Verification failed";
    setError(msg);
    showNotification(msg, NOTIFICATION_TYPES.ERROR);
  } finally {
    setOtpLoading(false);
  }
};


  const handleResendOTP = async () => {
    setOtpLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await api.post("/user/register/send-otp", {
        phone: formData.phone.trim(),
      });

      const responseData = result?.data || result;

      setSuccess("OTP resent successfully");
      setCountdown(30);
      showNotification(responseData?.message || "OTP resent successfully", NOTIFICATION_TYPES.SUCCESS);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to resend OTP";
      setError(msg);
      showNotification(msg, NOTIFICATION_TYPES.ERROR);
    } finally {
      setOtpLoading(false);
    }
  };

  // OTP Verification View
  if (step === "otp-verification") {
    return (
      <>
        {showRegisterSuccess && <SuccessAnimation message="Registration successful! Welcome to Ve go!" />}

        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="bg-indigo-100 p-3 rounded-full">
                  <ShieldCheckIcon className="h-8 w-8 text-indigo-600" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Phone</h2>
              <p className="text-gray-600 mb-2">Enter the 4-digit code sent to</p>
              <p className="text-indigo-600 font-semibold">+91 {formData.phone}</p>
              {developmentMode && <p className="text-sm text-green-600 mt-2 font-medium">Test OTP: 1234</p>}
            </div>

            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
              <div className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start animate-shake">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center animate-slide-down">
                    <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
                    <span className="text-sm">{success}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">
                    Enter Verification Code
                  </label>
                  <OTPInput length={4} onComplete={handleVerifyOTP} loading={otpLoading} showHint={developmentMode} />
                </div>

                <div className="flex justify-center space-x-4">
                  {countdown > 0 ? (
                    <div className="flex items-center text-gray-500 text-sm">
                      <ClockIcon className="h-4 w-4 mr-2" />
                      <span>Resend OTP in {countdown}s</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={otpLoading}
                      className="text-indigo-600 hover:text-indigo-500 font-medium disabled:opacity-50 transition-colors text-sm"
                    >
                      {otpLoading ? "Sending..." : "Resend OTP"}
                    </button>
                  )}
                </div>

                <div className="text-center pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("registration");
                      setError("");
                      setSuccess("");
                    }}
                    className="flex items-center justify-center text-gray-600 hover:text-gray-500 font-medium mx-auto transition-colors text-sm"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Change Phone Number
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Registration Form View
  return (
    <>
      {showOtpSuccess && <SuccessAnimation message="OTP - 1234!" />}
      {redirecting && <RedirectingAnimation message="Redirecting..." />}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-indigo-100 p-3 rounded-full">
                <UserIcon className="h-8 w-8 text-indigo-600" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Your Account</h2>
            <p className="mt-4 text-sm text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors">
                Sign in here
              </Link>
            </p>
          </div>

          <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
            <form onSubmit={handleInitialSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start animate-shake">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center animate-slide-down">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
                  <span className="text-sm">{success}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${
                        validationErrors.name ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-indigo-500"
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {validationErrors.name && <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${
                        validationErrors.email ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-indigo-500"
                      }`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  {validationErrors.email && <p className="text-xs text-red-600 mt-1">{validationErrors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <PhoneIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="absolute inset-y-0 left-10 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">+91</span>
                    </div>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={10}
                      required
                      className={`block w-full pl-16 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${
                        validationErrors.phone ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-indigo-500"
                      }`}
                      placeholder="9876543210"
                    />
                  </div>
                  {validationErrors.phone && <p className="text-xs text-red-600 mt-1">{validationErrors.phone}</p>}
                </div>

                {/* Alt phone */}
                <div>
                  <label htmlFor="altphone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Alternate Phone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="absolute inset-y-0 left-10 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">+91</span>
                    </div>
                    <input
                      id="altphone"
                      name="altphone"
                      type="tel"
                      value={formData.altphone}
                      onChange={handleChange}
                      maxLength={10}
                      required
                      className={`block w-full pl-16 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors ${
                        validationErrors.altphone ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-indigo-500"
                      }`}
                      placeholder="9876543210"
                    />
                  </div>
                  {validationErrors.altphone && <p className="text-xs text-red-600 mt-1">{validationErrors.altphone}</p>}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      id="address"
                      name="address"
                      rows={3}
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-colors resize-none ${
                        validationErrors.address ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-indigo-500"
                      }`}
                      placeholder="Enter your complete address"
                    />
                  </div>
                  {validationErrors.address && <p className="text-xs text-red-600 mt-1">{validationErrors.address}</p>}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={loading || Object.keys(validationErrors).length > 0}
                  className="w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-lg shadow-sm text-lg font-semibold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
                >
                  {loading ? (
                    <>
                      <ArrowPathIcon className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <ShieldCheckIcon className="h-5 w-5 mr-2" />
                      Register with OTP
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ✅ CSS animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-bounce {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes check-draw {
          from { stroke-dashoffset: 30; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes slide-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slide-up-delay {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        @keyframes slide-down {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-scale-bounce { animation: scale-bounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55); }
        .animate-check-draw { animation: check-draw 0.6s ease-out 0.3s forwards; }
        .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }
        .animate-slide-up { animation: slide-up 0.4s ease-out 0.5s both; }
        .animate-slide-up-delay { animation: slide-up-delay 0.4s ease-out 0.6s both; }
        .animate-spin-slow { animation: spin-slow 1.5s linear infinite; }
        .animate-shake { animation: shake 0.4s ease-in-out; }
        .animate-slide-down { animation: slide-down 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default Register;
